/** tm/components/ConsultEditModal.tsx — 약속 기본정보 수정 모달 */
import { useState, useEffect } from 'react';
import type { Consultation, CandidateSlot, WM, FP } from '../types';
import { C, T, R, Btn, Field } from './ui';
import * as api from '../api';
import PersonPicker from './PersonPicker';
import WmMultiPicker from './WmMultiPicker';
import { consultWmIds } from '../wmList';

function d10(d: string | null | undefined): string { if (!d) return ''; return d.slice(0, 10); }

interface Props {
  consultation: Consultation;
  wms: WM[];
  fps?: FP[];
  onDone: () => void;
  onCancel: () => void;
  onToast: (msg: string) => void;
}

interface FormState {
  fp_id: string;
  date: string;
  time: string;
  location: string;
  topic: string;
  notes: string;
  confirm_status: 'confirmed' | 'tentative';
  candidate_slots: CandidateSlot[];
  fee_type: 'paid' | 'free';
  fee_method: 'individual' | 'fa_settle' | '';
  fee_amount: string;
  fee_memo: string;
  wm_ids: string[];   // 함께한 WM 여러 명(첫 분 대표 · 2026-10-26)
  company_name: string;
  company_biz_no: string;
}

export default function ConsultEditModal({ consultation: c, wms, fps = [], onDone, onCancel, onToast }: Props) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>({
    fp_id: c.fp_id || '',
    date: d10(c.date),
    time: c.time || '',
    location: c.location || '',
    topic: c.topic || '',
    notes: c.notes || '',
    confirm_status: c.confirm_status || 'confirmed',
    candidate_slots: Array.isArray(c.candidate_slots) ? c.candidate_slots : [],
    fee_type: c.fee_type || 'paid',
    fee_method: c.fee_method || 'individual',
    fee_amount: String(c.fee_amount ?? 100000),
    fee_memo: c.fee_memo || '',
    wm_ids: consultWmIds(c),
    company_name: c.company_name || '',
    company_biz_no: '',
  });

  useEffect(() => {
    if (!c.company_id) return;
    const token = localStorage.getItem('token') || '';
    fetch('/api/tm/companies?limit=500', { headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' } })
      .then(r => r.json()).then(d => {
        const comp = (d.data?.companies || []).find((co: Record<string,unknown>) => co.id === c.company_id);
        if (comp) { upd('company_name', comp.name || ''); upd('company_biz_no', comp.biz_no || ''); }
      }).catch(() => {});
  }, [c.company_id]);

  const upd = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(p => ({ ...p, [k]: v }));

  function addSlot() {
    const firstLoc = form.candidate_slots.length > 0 ? form.candidate_slots[0].location : form.location;
    upd('candidate_slots', [...form.candidate_slots, { date: '', time: '', location: firstLoc }]);
  }
  function updSlot(idx: number, key: keyof CandidateSlot, val: string) {
    upd('candidate_slots', form.candidate_slots.map((s, i) => i === idx ? { ...s, [key]: val } : s));
  }
  function removeSlot(idx: number) { upd('candidate_slots', form.candidate_slots.filter((_, i) => i !== idx)); }

  async function handleSave() {
    if (form.confirm_status === 'confirmed' && !form.date) { onToast('날짜를 입력하세요'); return; }
    if (form.confirm_status === 'tentative' && form.candidate_slots.filter(s => s.date).length === 0) { onToast('후보 일정 1개 이상 입력'); return; }
    setSaving(true);
    try {
      if (c.company_id && (form.company_name.trim() || form.company_biz_no.trim())) {
        try { await api.updateCompany(c.company_id, { name: form.company_name.trim() || undefined, biz_no: form.company_biz_no.replace(/\D/g, '') || undefined }); } catch {}
      }
      await api.updateConsultation(c.id, {
        date: form.confirm_status === 'confirmed' ? form.date : (form.candidate_slots[0]?.date || ''),
        time: form.confirm_status === 'confirmed' ? form.time : '',
        location: form.location,
        topic: form.topic,
        notes: form.notes,
        confirm_status: form.confirm_status,
        candidate_slots: form.confirm_status === 'tentative' ? form.candidate_slots.filter(s => s.date) : [],
        fee_type: form.fee_type,
        fee_method: form.fee_type === 'paid' ? (form.fee_method || 'individual') : null,
        fee_amount: form.fee_type === 'paid' ? parseInt(form.fee_amount) || 100000 : 0,
        fee_memo: form.fee_memo,
        wm_ids: form.wm_ids,
        fp_id: form.fp_id || null,
      } as Record<string, unknown>);
      onToast('약속 정보가 수정되었습니다');
      onDone();
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '수정 실패'); }
    finally { setSaving(false); }
  }

  const secH: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#1B3A5C', paddingBottom: 8, borderBottom: '2px solid #E5E7EB', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onCancel} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.4)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 560, maxHeight: '92vh', background: '#fff', borderRadius: 16, display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', overflow: 'hidden' }}>

        {/* 헤더 */}
        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #111827, #1B3A5C)', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: T.lg, fontWeight: 700, flex: 1, color: '#fff' }}>✏️ 약속 수정</span>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
            {c.company_name || '업체'} · {c.fp_name || 'FP'}
          </div>
          <button onClick={onCancel} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', fontSize: T.md, color: '#fff' }}>✕</button>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* 업체 정보 */}
          <div style={{ marginBottom: 20 }}>
            <div style={secH}>🏢 업체 정보</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.sub, marginBottom: 3 }}>업체명</div>
                <input value={form.company_name} onChange={e => upd('company_name', e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.sub, marginBottom: 3 }}>사업자번호</div>
                <input value={form.company_biz_no} onChange={e => upd('company_biz_no', e.target.value)} placeholder="000-00-00000"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
              </div>
            </div>
          </div>

          {/* FP */}
          <div style={{ marginBottom: 20 }}>
            <div style={secH}>🧑‍💼 담당 FP</div>
            {/* 이름을 치면 찾아 넣고, 없으면 바로 등록 (2026-10-07) */}
            <PersonPicker kind="fp" people={fps} value={form.fp_id} onChange={id => upd('fp_id', id)} onToast={onToast} />
          </div>

          {/* WM */}
          <div style={{ marginBottom: 20 }}>
            <div style={secH}>🧑‍💼 담당 WM</div>
            <WmMultiPicker people={wms} value={form.wm_ids} onChange={ids => upd('wm_ids', ids)} onToast={onToast} />
          </div>

          {/* 일정 */}
          <div style={{ marginBottom: 20 }}>
            <div style={secH}>📅 일정</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button onClick={() => upd('confirm_status', 'confirmed')} style={{ flex: 1, padding: '8px 0', borderRadius: 10, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', border: `2px solid ${form.confirm_status === 'confirmed' ? '#16A34A' : C.border}`, background: form.confirm_status === 'confirmed' ? '#DCFCE7' : '#fff', color: form.confirm_status === 'confirmed' ? '#16A34A' : C.sub }}>● 확정</button>
              <button onClick={() => upd('confirm_status', 'tentative')} style={{ flex: 1, padding: '8px 0', borderRadius: 10, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', border: `2px solid ${form.confirm_status === 'tentative' ? '#D97706' : C.border}`, background: form.confirm_status === 'tentative' ? '#FEF3C7' : '#fff', color: form.confirm_status === 'tentative' ? '#D97706' : C.sub }}>● 미정</button>
            </div>
            {form.confirm_status === 'confirmed' && (
              <>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}><Field label="날짜" value={form.date} type="date" required onChange={v => upd('date', v)} /></div>
                  <div style={{ flex: 1 }}><Field label="시간" value={form.time} type="time" onChange={v => upd('time', v)} /></div>
                </div>
                <Field label="장소" value={form.location} placeholder="장소" onChange={v => upd('location', v)} />
              </>
            )}
            {form.confirm_status === 'tentative' && (
              <div>
                {form.candidate_slots.map((slot, i) => (
                  <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: T.sm, color: C.muted, width: 20, textAlign: 'center' }}>{i + 1}</span>
                    <input type="date" value={slot.date} onChange={e => updSlot(i, 'date', e.target.value)} style={{ flex: 1, padding: '9px 12px', height: 38, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' }} />
                    <input type="time" value={slot.time} onChange={e => updSlot(i, 'time', e.target.value)} style={{ width: 90, padding: '9px 12px', height: 38, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' }} />
                    <input value={slot.location} onChange={e => updSlot(i, 'location', e.target.value)} placeholder="장소" style={{ flex: 1, padding: '9px 12px', height: 38, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' }} />
                    <button onClick={() => removeSlot(i)} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: T.lg }}>✕</button>
                  </div>
                ))}
                <button onClick={addSlot} style={{ width: '100%', padding: 10, borderRadius: 10, border: `2px dashed ${C.border}`, background: '#fff', color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>+ 후보 일정 추가</button>
              </div>
            )}
          </div>

          {/* 상담료 */}
          <div style={{ marginBottom: 20 }}>
            <div style={secH}>💰 상담료</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {([['paid', '💰 지불'], ['free', '🎁 면제']] as const).map(([k, l]) => (
                <button key={k} onClick={() => upd('fee_type', k)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${form.fee_type === k ? (k === 'paid' ? C.accent : '#059669') : C.border}`, background: form.fee_type === k ? (k === 'paid' ? C.accentLt : '#DCFCE7') : '#fff', color: form.fee_type === k ? (k === 'paid' ? C.accent : '#059669') : C.sub }}>{l}</button>
              ))}
            </div>
            {form.fee_type === 'paid' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>금액</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input value={form.fee_amount} onChange={e => upd('fee_amount', e.target.value.replace(/\D/g, ''))} style={{ width: 100, padding: '9px 12px', height: 38, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                    <span style={{ fontSize: T.sm, color: C.muted }}>원</span>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>정산</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {([['individual', '개별입금'], ['fa_settle', 'FA정산']] as const).map(([k, l]) => (
                      <button key={k} onClick={() => upd('fee_method', k)} style={{ flex: 1, padding: 0, height: 38, borderRadius: 8, fontSize: T.xs, fontWeight: form.fee_method === k ? 700 : 400, cursor: 'pointer', border: `1px solid ${form.fee_method === k ? C.accent : C.border}`, background: form.fee_method === k ? C.accentLt : '#fff', color: form.fee_method === k ? C.accent : C.sub }}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {form.fee_type === 'free' && <input value={form.fee_memo} onChange={e => upd('fee_memo', e.target.value)} placeholder="면제 사유" style={{ width: '100%', padding: '9px 12px', height: 38, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' }} />}
          </div>

          {/* 주제/메모 */}
          <div>
            <div style={secH}>📝 상담 주제</div>
            <Field label="주제" value={form.topic} placeholder="상담 주제" onChange={v => upd('topic', v)} />
            <Field label="사전 메모" value={form.notes} component="textarea" placeholder="메모 (선택)" onChange={v => upd('notes', v)} />
          </div>
        </div>

        {/* 하단 */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8 }}>
          <Btn variant="secondary" onClick={onCancel} style={{ flex: 1 }}>취소</Btn>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, padding: '10px 0', background: saving ? C.muted : 'linear-gradient(135deg, #111827, #1B3A5C)', color: '#fff', fontWeight: 700, borderRadius: 10, border: 'none', fontSize: T.md, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? '⏳ 저장 중...' : '💾 수정 완료'}
          </button>
        </div>
      </div>
    </div>
  );
}
